<template>
  <div v-if="visible" class="absolute w-full" :style="{ top }">
    <div v-if="pill" class="absolute whitespace-no-wrap transform translate-x-[4px] -translate-y-1/2 bg-red-500 text-white text-[0.6rem] leading-loose px-2 rounded-lg z-50">
      {{ time }}
    </div>

    <div class="absolute h-2px transition duration-1000 ease-in-out pointer-events-none w-full z-40" :class="{ 'pl-3' : pill }">
      <div class="bg-red-500 h-2px w-full" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { isSameWeek } from 'date-fns'

const { $auth, $date, $dateFns } = useNuxtApp()

interface Props {
  provider: any
  pill?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  pill: false,
})

const time = ref<string | null>(null)
const top = ref<string | null>(null)

const minuteSize = computed(() => {
  const value = Number($auth?.info?.minuteSize)
  return Number.isFinite(value) && value > 0 ? value : 2
})

const scheduleView = computed(() => {
  const value = $auth.active?.scheduleView
  if (typeof value === 'string') {
    return value
  }
  if (typeof value?.value === 'string') {
    return value.value
  }

  return '/schedule/day'
})

function provideDates() {
  const dates = props.provider?.dates ?? []
  const offset = $auth.info?.location.timeZoneOffset || 0
  const date = new Date(new Date().toUTCString().slice(0, -4))

  const currentDate = $date.add(date, { seconds: offset })
  const displayDate = dates[0]?.date?.value ? $date.parse(dates[0].date.value) : currentDate

  return { currentDate, displayDate }
}

const isToday = computed(() => {
  const { currentDate, displayDate } = provideDates()
  return $dateFns.isSameDay(currentDate, displayDate)
})

const isThisWeek = computed(() => {
  const { currentDate, displayDate } = provideDates()
  return isSameWeek(currentDate, displayDate)
})

const visible = computed(() => {
  return (scheduleView.value === '/schedule/day' && isToday.value) || (scheduleView.value === '/schedule/week' && isThisWeek.value)
})

let timer: ReturnType<typeof setTimeout> | null = null

function updateTime() {
  const offset = $auth.info?.location.timeZoneOffset || 0
  const date = new Date(new Date().toUTCString().slice(0, -4))
  const t = $date.add(date, { seconds: offset })
  const minutes = $dateFns.getMinutes(t)
  const hours = $dateFns.getHours(t)

  const total = (hours * 60) + minutes

  time.value = $date.format(t, $date.presets.time)
  top.value = `${total * minuteSize.value}px`

  timer = setTimeout(updateTime, 10000)
}

onMounted(updateTime)

onBeforeUnmount(() => {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
})
</script>
